import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Unified Campaigns index — lists both Delivery (candidate) and BD (outreach)
// campaigns. See docs/campaigns-workspace-design.md.

type Mode = '' | 'delivery' | 'bd';

const MODE_LABEL: Record<string, string> = { delivery: 'Delivery', bd: 'BD Outreach' };
const MODE_BADGE: Record<string, string> = {
  delivery: 'bg-blue-100 text-blue-700',
  bd: 'bg-purple-100 text-purple-700',
};

const CampaignsIndex: React.FC = () => {
  const { token, projects } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const modeParam = (searchParams.get('mode') as Mode) || '';

  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...(modeParam && { mode: modeParam }),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search }),
      });
      const resp = await fetch(`/api/campaigns?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      setCampaigns(await resp.json());
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [token, modeParam, statusFilter, search]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const setMode = (m: Mode) => {
    const next = new URLSearchParams(searchParams);
    if (m) next.set('mode', m); else next.delete('mode');
    setSearchParams(next, { replace: true });
  };

  const statusColors: Record<string, string> = {
    open: 'bg-green-100 text-green-700',
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    'on-hold': 'bg-yellow-100 text-yellow-700',
    completed: 'bg-gray-100 text-gray-500',
    closed: 'bg-gray-100 text-gray-500',
    filled: 'bg-gray-100 text-gray-500',
  };

  const stats = {
    total: campaigns.length,
    totalTargets: campaigns.reduce((sum, c) => sum + (parseInt(c.total_targets) || 0), 0),
  };

  const modeTabs: { key: Mode; label: string }[] = [
    { key: '', label: 'All' },
    { key: 'delivery', label: 'Delivery' },
    { key: 'bd', label: 'BD Outreach' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Campaigns</h1>
            <p className="text-sm text-gray-500">
              {stats.total} campaign{stats.total !== 1 ? 's' : ''} · {stats.totalTargets} in pipelines
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Campaign
        </button>
      </div>

      {/* Mode tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        {modeTabs.map(tab => (
          <button
            key={tab.key || 'all'}
            onClick={() => setMode(tab.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              modeParam === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex-1 min-w-[280px] max-w-md">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, company, candidate..."
              className="w-full pl-10 pr-4 py-2 text-sm input"
            />
          </div>
        </form>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-sm input bg-white"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card p-12 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No campaigns yet</h3>
          <p className="text-gray-500 mb-4">Create your first campaign to start tracking outreach.</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map(campaign => {
            const subtitle = campaign.mode === 'bd'
              ? campaign.candidate_name
              : [campaign.company_name, campaign.location].filter(Boolean).join(' · ');
            return (
              <button
                key={campaign.id}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                className="card card-hover p-5 text-left group"
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 truncate">
                      {campaign.name}
                    </h3>
                    {subtitle && <p className="text-sm text-gray-500 truncate mt-0.5">{subtitle}</p>}
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${MODE_BADGE[campaign.mode] || 'bg-gray-100 text-gray-600'}`}>
                    {MODE_LABEL[campaign.mode] || campaign.mode}
                  </span>
                </div>

                {campaign.description && (
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{campaign.description}</p>
                )}

                {campaign.stages && campaign.stages.length > 0 && (
                  <div className="flex gap-1 mb-3">
                    {campaign.stages.map((s: any) => (
                      <div key={s.id} className="flex-1 text-center" title={`${s.name}: ${s.count}`}>
                        <div className="h-1.5 rounded-full mb-1" style={{ backgroundColor: s.count > 0 ? s.color : '#E5E7EB' }} />
                        <span className="text-[10px] text-gray-400">{s.count > 0 ? s.count : ''}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className={`font-medium px-2 py-0.5 rounded-full ${statusColors[campaign.status] || 'bg-gray-100 text-gray-500'}`}>
                    {campaign.status ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1) : '—'}
                  </span>
                  <span>{parseInt(campaign.total_targets) || 0} in pipeline</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateCampaignModal
          token={token}
          projects={projects}
          defaultMode={modeParam || 'delivery'}
          onClose={() => setShowCreateModal(false)}
          onCreated={(campaign: any) => {
            setShowCreateModal(false);
            navigate(`/campaigns/${campaign.id}`);
          }}
        />
      )}
    </div>
  );
};


// ─── Create Campaign Modal ─────────────────────────────────────────────

const CreateCampaignModal: React.FC<{
  token: string | null;
  projects: { id: number; name: string }[];
  defaultMode: string;
  onClose: () => void;
  onCreated: (campaign: any) => void;
}> = ({ token, projects, defaultMode, onClose, onCreated }) => {
  const [mode, setMode] = useState<string>(defaultMode === 'bd' ? 'bd' : 'delivery');
  const [projectId, setProjectId] = useState<string>(projects.length === 1 ? String(projects[0].id) : '');
  const [form, setForm] = useState({ name: '', description: '', company_name: '', location: '' });
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateResults, setCandidateResults] = useState<any[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<{ id: number; name: string } | null>(null);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!candidateSearch.trim() || !token) { setCandidateResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearchingCandidates(true);
      try {
        const resp = await fetch(`/api/global/candidates?search=${encodeURIComponent(candidateSearch)}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          setCandidateResults(Array.isArray(data) ? data : data.data || []);
        }
      } catch (err) {
        console.error('Candidate search failed:', err);
      } finally {
        setSearchingCandidates(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [candidateSearch, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !token) return;
    if (!projectId) { alert('Please choose a project for this campaign.'); return; }
    setSaving(true);
    try {
      const body: any = { mode, name: form.name, description: form.description, project_id: parseInt(projectId) };
      if (mode === 'delivery') {
        body.company_name = form.company_name || null;
        body.location = form.location || null;
      } else {
        body.candidate_id = selectedCandidate?.id || null;
        body.candidate_name = selectedCandidate?.name || null;
      }
      const resp = await fetch('/api/campaigns', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || errData.error || 'Failed to create');
      }
      onCreated(await resp.json());
    } catch (err: any) {
      console.error('Failed to create campaign:', err);
      alert(`Failed to create campaign: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="modal-dialog w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200/80">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Create Campaign</h2>
            <p className="text-sm text-gray-500 mt-1">Delivery campaigns target candidates; BD campaigns target companies &amp; contacts.</p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Mode selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div className="grid grid-cols-2 gap-2">
                {(['delivery', 'bd'] as const).map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      mode === m ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {m === 'delivery' ? 'Candidate Delivery' : 'BD Outreach'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className="input text-sm">
                <option value="">Select a project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-400">Campaign members come from this project.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={mode === 'delivery' ? 'e.g. Senior DevOps Engineer - Acme' : 'e.g. DevOps Engineers - Financial Services'}
                className="input text-sm"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Target market, goals, notes..."
                rows={3}
                className="input text-sm resize-none"
              />
            </div>

            {mode === 'delivery' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="input text-sm"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Candidate <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">Attach a candidate you're remarketing to this campaign.</p>
                {selectedCandidate ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold">
                      {selectedCandidate.name[0]?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-blue-700 flex-1">{selectedCandidate.name}</span>
                    <button
                      type="button"
                      onClick={() => { setSelectedCandidate(null); setCandidateSearch(''); }}
                      className="p-0.5 hover:bg-blue-100 rounded text-blue-400 hover:text-blue-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={candidateSearch}
                      onChange={e => setCandidateSearch(e.target.value)}
                      placeholder="Search candidates by name..."
                      className="input text-sm"
                    />
                    {candidateSearch.trim() && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200/80 rounded-xl shadow-elevated max-h-48 overflow-y-auto animate-fade-in">
                        {searchingCandidates ? (
                          <div className="px-4 py-3 text-center text-sm text-gray-400">Searching...</div>
                        ) : candidateResults.length === 0 ? (
                          <div className="px-4 py-3 text-center text-sm text-gray-400">No candidates found</div>
                        ) : (
                          candidateResults.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => { setSelectedCandidate({ id: c.id, name: c.name }); setCandidateSearch(''); setCandidateResults([]); }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-sm"
                            >
                              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {c.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-gray-900 truncate block">{c.name}</span>
                                {(c.current_position || c.current_company) && (
                                  <span className="text-xs text-gray-500 truncate block">
                                    {c.current_position}{c.current_company ? ` at ${c.current_company}` : ''}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={saving || !form.name.trim()} className="btn-primary text-sm">
                {saving ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};


export default CampaignsIndex;
